import { useState, useRef, useEffect, useCallback } from 'react';
import { startSpeechRecognition, requestSpeechPermission } from '../lib/speechRecognition';
import { getNetworkStatus, saveToOfflineQueue } from '../lib/capacitor';
import { voiceApi, VoiceAnalysis, VoiceAnalyzeParams } from '../api/voice';

export type VoiceState = 'idle' | 'recording' | 'processing' | 'done' | 'error';

export interface UseVoiceRecordingOptions {
  reportId?: string;
  reportItemId?: string;
  roomId?: string;
  componentId?: string;
  inspectionId?: string;
  context?: VoiceAnalyzeParams['context'];
}

export interface UseVoiceRecordingResult {
  state: VoiceState;
  transcript: string;
  analysis: VoiceAnalysis | null;
  error: string | null;
  elapsedSeconds: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  retryAnalysis: () => Promise<void>;
  reset: () => void;
}

export function useVoiceRecording(
  options: UseVoiceRecordingOptions = {}
): UseVoiceRecordingResult {
  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [analysis, setAnalysis] = useState<VoiceAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const stopFnRef = useRef<(() => void) | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  // Accumulates committed (isFinal=true) phrases; kept separate from the displayed
  // transcript so interim results don't get appended twice when they're finalized.
  const finalTextRef = useRef<string>('');

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimer();
      stopFnRef.current?.();
    };
  }, [clearTimer]);

  const startRecording = useCallback(async () => {
    const granted = await requestSpeechPermission();
    if (!granted) {
      setState('error');
      setError('Microphone permission denied.');
      return;
    }

    setTranscript('');
    setAnalysis(null);
    setError(null);
    setElapsedSeconds(0);
    finalTextRef.current = '';
    setState('recording');

    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    stopFnRef.current = startSpeechRecognition({
      onResult: ({ transcript: t, isFinal }) => {
        if (isFinal) {
          // Commit phrase to accumulated finals; never show the interim again
          finalTextRef.current = finalTextRef.current
            ? finalTextRef.current + ' ' + t
            : t;
          setTranscript(finalTextRef.current);
        } else {
          // Show committed finals + the current in-progress interim
          setTranscript(
            finalTextRef.current ? finalTextRef.current + ' ' + t : t
          );
        }
      },
      onEnd: () => {
        /* handled by stopRecording */
      },
      onError: (err) => {
        clearTimer();
        setState('error');
        setError(`Speech recognition error: ${err}`);
      },
    });
  }, [clearTimer]);

  const stopRecording = useCallback(async () => {
    clearTimer();
    stopFnRef.current?.();
    stopFnRef.current = null;
    setState('processing');

    const finalTranscript = transcript.trim();

    if (!finalTranscript) {
      setState('error');
      setError('No speech detected. Please try again.');
      return;
    }

    // Check network before attempting API call
    const network = await getNetworkStatus();
    if (!network.connected) {
      const queueKey = `voice_queue_${Date.now()}`;
      await saveToOfflineQueue(queueKey, {
        transcript: finalTranscript,
        ...options,
        queuedAt: new Date().toISOString(),
      });
      setState('done');
      setError('Saved to offline queue — will sync when connected.');
      return;
    }

    try {
      const analyzePromise = voiceApi.analyze({
        transcript: finalTranscript,
        reportId: options.reportId,
        reportItemId: options.reportItemId,
        roomId: options.roomId,
        componentId: options.componentId,
        inspectionId: options.inspectionId,
        context: options.context,
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out — tap "Retry Analysis" to try again')), 30_000)
      );
      const result = await Promise.race([analyzePromise, timeoutPromise]);

      if (result.success && result.data) {
        setAnalysis(result.data);
        setState('done');
      } else {
        throw new Error(result.error ?? 'Analysis failed');
      }
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to analyze voice memo');
    }
  }, [transcript, options, clearTimer]);

  // Retry the API analysis step without re-recording (preserves transcript)
  const retryAnalysis = useCallback(async () => {
    const savedTranscript = finalTextRef.current || transcript;
    if (!savedTranscript.trim()) return;

    setState('processing');
    setError(null);

    try {
      const analyzePromise = voiceApi.analyze({
        transcript: savedTranscript,
        reportId: options.reportId,
        reportItemId: options.reportItemId,
        roomId: options.roomId,
        componentId: options.componentId,
        inspectionId: options.inspectionId,
        context: options.context,
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out — tap "Retry Analysis" to try again')), 30_000)
      );
      const result = await Promise.race([analyzePromise, timeoutPromise]);

      if (result.success && result.data) {
        setAnalysis(result.data);
        setState('done');
      } else {
        throw new Error(result.error ?? 'Analysis failed');
      }
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to analyze voice memo');
    }
  }, [transcript, options]);

  const reset = useCallback(() => {
    clearTimer();
    stopFnRef.current?.();
    stopFnRef.current = null;
    setState('idle');
    setTranscript('');
    setAnalysis(null);
    setError(null);
    setElapsedSeconds(0);
    finalTextRef.current = '';
  }, [clearTimer]);

  return {
    state,
    transcript,
    analysis,
    error,
    elapsedSeconds,
    startRecording,
    stopRecording,
    retryAnalysis,
    reset,
  };
}
